import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useKeyboardSave } from './use-keyboard-save';
import type { ConstantElement, RegoValue } from '@/types/policy-editor';

interface ConstantFormProps {
    element: ConstantElement | null;
    onSave: (element: ConstantElement) => void;
    onCancel: () => void;
}

export function ConstantForm({ element, onSave, onCancel }: ConstantFormProps) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [isDefault, setIsDefault] = useState(false);
    const [valueText, setValueText] = useState('');

    useEffect(() => {
        if (element) {
            setName(element.name);
            setDescription(element.description ?? '');
            setIsDefault(element.isDefault ?? false);
            setValueText(serializeValue(element.value));
        } else {
            setName('');
            setDescription('');
            setIsDefault(false);
            setValueText('');
        }
    }, [element]);

    const handleSave = useCallback(() => {
        if (!name.trim()) return;
        const value = parseValueText(valueText);
        onSave({
            id: element?.id ?? `el-${Date.now()}`,
            elementType: 'constant',
            name,
            description: description || undefined,
            value,
            isDefault,
        });
    }, [element, name, description, valueText, isDefault, onSave]);

    useKeyboardSave(handleSave, !!name.trim());

    return (
        <div className="flex h-full flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto p-4">
                <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
                    A data block defines a constant or default value. Use it for configuration
                    data, lookup tables, or fallback values that other rules reference.
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="const-name">Name</Label>
                    <Input
                        id="const-name"
                        placeholder="e.g. llm_models, default_profile_settings"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="const-desc">Description</Label>
                    <Input
                        id="const-desc"
                        placeholder="What this data block configures"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </div>

                <div className="flex items-center gap-2">
                    <Switch id="const-default" checked={isDefault} onCheckedChange={setIsDefault} />
                    <Label htmlFor="const-default" className="text-sm">
                        Default value (used when no other rule matches)
                    </Label>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="const-value">Value (JSON format, or <code className="text-[10px] bg-slate-100 px-1 rounded">set()</code> for empty Rego sets)</Label>
                    <Textarea
                        id="const-value"
                        className="min-h-[200px] font-mono text-xs"
                        placeholder='{"key": "value"}'
                        value={valueText}
                        onChange={(e) => setValueText(e.target.value)}
                    />
                </div>
            </div>

            <div className="flex items-center justify-between border-t px-4 py-3">
                <span className="text-[10px] text-slate-400">Ctrl+Enter to save</span>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
                    <Button size="sm" onClick={handleSave} disabled={!name.trim()}>Save</Button>
                </div>
            </div>
        </div>
    );
}

function serializeValue(val: RegoValue): string {
    switch (val.kind) {
        case 'scalar':
            return JSON.stringify(val.value);
        case 'object':
            try {
                const obj: Record<string, unknown> = {};
                for (const entry of val.entries) obj[entry.key] = regoValueToJson(entry.value);
                return JSON.stringify(obj, null, 2);
            } catch { return '{}'; }
        case 'array':
            return JSON.stringify(val.items.map(regoValueToJson), null, 2);
        case 'set':
            if (val.items.length === 0) return 'set()';
            return 'set(' + JSON.stringify(val.items.map(regoValueToJson), null, 2) + ')';
        case 'raw':
            return val.text;
        default:
            return '';
    }
}

function regoValueToJson(val: RegoValue): unknown {
    switch (val.kind) {
        case 'scalar': return val.value;
        case 'object': {
            const obj: Record<string, unknown> = {};
            for (const e of val.entries) obj[e.key] = regoValueToJson(e.value);
            return obj;
        }
        case 'array': return val.items.map(regoValueToJson);
        case 'set': return val.items.map(regoValueToJson);
        case 'reference': return val.path;
        case 'raw': return val.text;
        default: return null;
    }
}

function parseValueText(text: string): RegoValue {
    const trimmed = text.trim();
    if (!trimmed) return { kind: 'scalar', value: null };

    if (trimmed === 'set()') {
        return { kind: 'set', items: [] };
    }

    const setMatch = trimmed.match(/^set\(\s*([\s\S]*)\s*\)$/);
    if (setMatch) {
        try {
            const inner = JSON.parse(setMatch[1]);
            if (Array.isArray(inner)) {
                return { kind: 'set', items: inner.map(jsonToRegoValue) };
            }
        } catch { /* fall through */ }
    }

    try {
        return jsonToRegoValue(JSON.parse(trimmed));
    } catch { return { kind: 'raw', text: trimmed }; }
}

function jsonToRegoValue(val: unknown): RegoValue {
    if (val === null) return { kind: 'scalar', value: null };
    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
        return { kind: 'scalar', value: val };
    }
    if (Array.isArray(val)) return { kind: 'array', items: val.map(jsonToRegoValue) };
    if (typeof val === 'object') {
        return {
            kind: 'object',
            entries: Object.entries(val as Record<string, unknown>).map(([k, v]) => ({
                key: k, value: jsonToRegoValue(v),
            })),
        };
    }
    return { kind: 'scalar', value: null };
}
